import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { ApolloError, useApolloClient } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import styled, { css } from 'styled-components';
import {
    Box,
    Checkbox,
    Link,
    Matrix,
    MatrixItem,
    Select,
    Stack,
    Text,
    useFloatingAlert,
} from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces } from '@99designs/emu/settings';
import {
    FormattedHTMLMessage,
    FormattedMessage,
    formatMessage,
} from '@99designs/frontend-components/i18n';
import { getApolloErrorType } from '@99designs/spa-common';
import { useSite } from '@99designs/spa-common';
import { trackEvent } from '@99designs/tracking';
import PrintValidatorContext from '../../../contexts/PrintValidatorContext';
import { ObservableForm } from '../../ObservableForm';
import {
    VistaprintProductDocument,
    VistaprintProductQuery,
    useUpdateOrCreatePrintMutation,
} from './VistaprintProduct.generated';

const EVENT_PRINT_SERVICE_OPTION_SELECTED = 'Project brief print service option selected';

const StyledMarketingImage = styled.img<{ isMobile?: boolean }>`
    border-radius: 15px;
    width: 100%;
    height: 0;
    vertical-align: middle;
    min-height: 100%;
    object-fit: cover;

    height: ${({ isMobile }) => isMobile && '123px'};
`;

const MarketingImgContainer = styled(Box)<{
    isPrinting: boolean;
    isMobile?: boolean;
    $height?: number;
}>`
    filter: ${({ isPrinting }) => !isPrinting && 'grayscale(100%)'};

    display: ${({ isMobile }) => (isMobile ? 'none' : 'block')};

    ${({ isMobile, $height }) => !isMobile && $height && `height: ${$height}px;`};

    ${mediaQuery.between('small', 'medium')} {
        display: ${({ isMobile }) => (isMobile ? 'block' : 'none')};
    }
`;

const SelectboxStyling = styled(Box)`
    > div:nth-of-type(1) > div:nth-of-type(1) {
        padding-bottom: ${spaces['0.25bu']};
    }
`;

const StyleSelectOptions = styled(Select)<{ disabled: boolean }>`
    ${({ disabled }) =>
        disabled &&
        css`
            background: ${colors.grey100};
            border-color: ${colors.grey300};
        `}
    color: ${({ disabled }) => (disabled ? colors.grey500 : colors.grey800)};
`;

const NewBadge = styled(Text)<{ isPrinting: boolean }>`
    background: #fbbf72;
    border-radius: 25px;
    display: inline;
    flex-direction: row;
    padding: 4px 12px;

    ${({ isPrinting }) =>
        !isPrinting &&
        css`
            background-color: ${colors.grey100};
            border-color: ${colors.grey300};
            color: ${colors.grey500};
        `}
`;

function isValidOption(
    compatibilityList: string[],
    selections: Record<string, string | undefined>,
    attributeKey: string,
    optionValue: string
) {
    const selectionPairs = Object.keys(selections)
        .filter((key) => key !== attributeKey && selections[key])
        .map((key) => `${key}:${selections[key]}`);
    const optionPair = `${attributeKey}:${optionValue}`;

    return compatibilityList.some(
        (c) => selectionPairs.every((s) => c.includes(s)) && c.includes(optionPair)
    );
}

function getFirstSelections(
    productAttributes: VistaprintProductQuery['vistaprintProduct']['attributes'],
    selections: Record<string, string>,
    index: number
) {
    const aboveAttributes = productAttributes.slice(0, index);

    return aboveAttributes.reduce<Record<string, string>>((s, { key: k }) => {
        if (selections[k]) {
            s[k] = selections[k];
        }

        return s;
    }, {});
}

interface Props<T> {
    form: ObservableForm<T>;
    category: string;
    mpvId?: string;
    briefId: string;
    subtext: string;
    product: VistaprintProductQuery['vistaprintProduct'];
    printRecord?: VistaprintProductQuery['printByExternalId'];
    image: string;
}

interface Fields {
    printServiceOptions: string;
}

export function VistaprintOptions<T extends Fields>({
    form,
    subtext,
    product,
    image: vistaprintLogoText,
    briefId,
    category,
    mpvId,
    printRecord,
}: Props<T>) {
    const {
        attributes: productAttributes,
        marketingImage,
        compatibleAttributes,
        vpProductKey,
    } = product;
    const selectedAttributesMap = productAttributes.reduce<Record<string, string>>((acc, obj) => {
        if (printRecord?.vpProductKey === vpProductKey) {
            const preselectedAttribute = printRecord?.options.find((attr) => obj.key === attr.key);

            if (preselectedAttribute?.value) {
                acc[obj.key] = preselectedAttribute.value;
            }
        }

        return acc;
    }, {});

    const [formSelections, setFormSelections] = useState(selectedAttributesMap);
    const [errorFields, setErrorFields] = useState<string[]>([]);

    const alert = useFloatingAlert();
    const site = useSite();
    const client = useApolloClient();
    const [updateOrCreatePrintOptions] = useUpdateOrCreatePrintMutation({
        onError(error: ApolloError) {
            alert({
                type: 'error',
                message: __('Sorry, something went wrong. Please refresh the page and try again.'),
            });
            Bugsnag.notify(error, (event) => {
                const errorType = getApolloErrorType(error);
                event.addMetadata('apollo', 'errorType', errorType);
                event.context = 'Setting selected print option mutation';
            });
        },
        onCompleted: ({ updateOrCreatePrint }) => {
            if (!printRecord) {
                client.writeQuery({
                    query: VistaprintProductDocument,
                    variables: {
                        ...(mpvId ? { mpvId } : { category }),
                        locale: site.siteConfig.locale,
                        externalId: briefId,
                    },
                    data: {
                        vistaprintProduct: product,
                        printByExternalId: updateOrCreatePrint,
                    },
                });
            }
        },
    });

    const isPrinting = form.get('printServiceOptions') === 'VISTAPRINT';
    const { setPrintValidator } = useContext(PrintValidatorContext);

    useEffect(() => {
        const fieldValidator = () => {
            if (!isPrinting) {
                return [];
            }

            const eFields = productAttributes
                .map((attribute) => (formSelections[attribute.key] ? undefined : attribute.key))
                .filter((f): f is string => f !== undefined);

            setErrorFields(eFields);

            return eFields;
        };

        setPrintValidator?.(fieldValidator);

        return () => {
            setPrintValidator?.(undefined);
        };
    }, [productAttributes, formSelections, setPrintValidator, isPrinting]);

    const handleSelectedOptions = (selectedOptions: Record<string, string>) => {
        return updateOrCreatePrintOptions({
            variables: {
                input: {
                    ...(mpvId ? { mpvId } : { categoryKey: category }),
                    externalId: briefId,
                    locale: site.siteConfig.locale,
                    options: Object.entries(selectedOptions).map(([key, value]) => ({
                        key,
                        value,
                    })),
                },
            },
        });
    };

    const updateSelection = (attributeKey: string, optionKey: string) => {
        const newSelections = {
            ...formSelections,
            [attributeKey]: optionKey,
        };

        const attributeKeyIndex = productAttributes.findIndex((a) => a.key === attributeKey);

        productAttributes.slice(attributeKeyIndex + 1).forEach((a, i) => {
            if (!newSelections[a.key]) {
                return;
            }

            const aboveSelections = getFirstSelections(
                productAttributes,
                newSelections,
                attributeKeyIndex + 1 + i
            );

            const isValid = isValidOption(
                compatibleAttributes,
                aboveSelections,
                a.key,
                newSelections[a.key]
            );

            if (!isValid) {
                delete newSelections[a.key];
            }
        });

        setErrorFields((efs) => {
            return efs.filter((ef) => newSelections[ef] === undefined);
        });
        setFormSelections(newSelections);
        handleSelectedOptions(newSelections);
    };

    useEffect(() => {
        handleSelectedOptions(formSelections);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // leave as empty array as we only want to run handleSelectedOptions when the component mounts

    const marketingImgRef = useRef<HTMLDivElement>(null);
    const [marketingImgAnchorHeight, setMarketingImgAnchorHeight] = useState<number>();

    useEffect(() => {
        setMarketingImgAnchorHeight(marketingImgRef.current?.getBoundingClientRect().height);
    }, []);

    const handlePrintServiceOptionChange = () => {
        const printServiceOption = isPrinting ? 'NOT_SURE' : 'VISTAPRINT';
        form.set('printServiceOptions', printServiceOption);

        if (printServiceOption === 'NOT_SURE' && errorFields.length > 0) {
            setErrorFields([]);
        }

        trackEvent(EVENT_PRINT_SERVICE_OPTION_SELECTED, {
            brief_id: briefId,
            service_type: category,
            print_service: printServiceOption,
        });
    };

    return (
        <>
            <Box marginBottom="0.75bu">
                <NewBadge isPrinting={isPrinting} size="size2" weight="bold" fontFamily="heading">
                    {__('New')}
                </NewBadge>
            </Box>
            <Matrix columns={[1, 2]} space={'2bu'}>
                <MatrixItem>
                    <Box display="block" marginBottom="0.25bu">
                        <Text size="size5" weight="bold" fontFamily="heading">
                            <FormattedHTMLMessage
                                message={__(
                                    'Print with <img src={vistaprintLogoText} alt="Vistaprint logo" />'
                                )}
                                values={{
                                    vistaprintLogoText,
                                }}
                            />
                        </Text>
                    </Box>
                    <Box marginBottom="0.75bu" marginTop="0.75bu">
                        <Text>{subtext}</Text>
                    </Box>
                    <MarketingImgContainer isPrinting={isPrinting} isMobile marginBottom="0.75bu">
                        <Box width="100%" height="100%">
                            <StyledMarketingImage
                                src={marketingImage}
                                alt=""
                                role="presentation"
                                isMobile
                            />
                        </Box>
                    </MarketingImgContainer>
                    <Stack space="0.5bu">
                        {productAttributes.map((attributeOption, i) => {
                            const attributeMap = attributeOption.values.map((option) => {
                                return {
                                    disabled: !isValidOption(
                                        compatibleAttributes,
                                        getFirstSelections(productAttributes, formSelections, i),
                                        attributeOption.key,
                                        option.key
                                    ),
                                    label: option.name,
                                    value: option.key,
                                };
                            });

                            return (
                                <SelectboxStyling key={attributeOption.key}>
                                    <StyleSelectOptions
                                        error={
                                            errorFields.includes(attributeOption.key)
                                                ? __('Please select an option')
                                                : undefined
                                        }
                                        placeholder={formatMessage(__('Select {attribute}'), {
                                            attribute: attributeOption.summaryName.toLowerCase(),
                                        })}
                                        disabled={!isPrinting}
                                        id={`print-option-${attributeOption.key}`}
                                        label={attributeOption.summaryName}
                                        onChange={(e) => {
                                            updateSelection(
                                                attributeOption.key,
                                                e?.value as string
                                            );
                                        }}
                                        options={attributeMap}
                                        value={
                                            attributeMap.find(
                                                (o) =>
                                                    formSelections?.[attributeOption.key] ===
                                                    o.value
                                            ) ?? null
                                        }
                                        data-testid={`select-dropdown-${i}`}
                                    />
                                </SelectboxStyling>
                            );
                        })}
                    </Stack>
                </MatrixItem>
                <MarketingImgContainer
                    isPrinting={isPrinting}
                    ref={marketingImgRef}
                    $height={marketingImgAnchorHeight}
                >
                    <Box width="100%" height="100%">
                        <StyledMarketingImage src={marketingImage} alt="" role="presentation" />
                    </Box>
                </MarketingImgContainer>
            </Matrix>
            <Box marginTop="1bu">
                {printRecord?.estimatedPrice && (
                    <Box data-testid="quantity-price">
                        <FormattedMessage
                            message={__(
                                '{quantity} starting at {priceWithCurrency} with VistaPrint.'
                            )}
                            values={{
                                quantity: (
                                    <Text weight="bold">{printRecord.estimatedPrice.quantity}</Text>
                                ),
                                priceWithCurrency: (
                                    <Text weight="bold">
                                        <FormattedNumber
                                            value={printRecord.estimatedPrice.price}
                                            // eslint-disable-next-line react/style-prop-object
                                            style="currency"
                                            currency={printRecord.estimatedPrice.currency}
                                        />
                                    </Text>
                                ),
                            }}
                        />
                    </Box>
                )}
                <Box>
                    <Link
                        href={`https://www.${site
                            .getDomain()
                            .replace('99designs', 'vistaprint')}/satisfaction-guarantee`}
                        openInNewTab
                        color={colors.grey800}
                        size="size3"
                    >
                        {__('100% satisfaction guaranteed.')}
                    </Link>
                </Box>
            </Box>
            <Box marginTop="2bu">
                <Checkbox
                    id="no-print-selected"
                    label={__('I am not printing with VistaPrint')}
                    onChange={handlePrintServiceOptionChange}
                    checked={!isPrinting}
                />
            </Box>
        </>
    );
}
